import React from "react";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { fmtINR } from "../lib/api";
import { Loader2, Inbox } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  collected: "bg-emerald-50 text-emerald-700 border-emerald-200",
  online: "bg-emerald-50 text-emerald-700 border-emerald-200",
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  partially_collected: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  visited: "bg-amber-50 text-amber-700 border-amber-200",
  processing: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-sky-50 text-sky-700 border-sky-200",
  submitted: "bg-sky-50 text-sky-700 border-sky-200",
  assigned: "bg-sky-50 text-sky-700 border-sky-200",
  open: "bg-sky-50 text-sky-700 border-sky-200",
  mapping: "bg-sky-50 text-sky-700 border-sky-200",
  validating: "bg-sky-50 text-sky-700 border-sky-200",
  reassigned: "bg-violet-50 text-violet-700 border-violet-200",
  reassessment_initiated: "bg-violet-50 text-violet-700 border-violet-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  escalated: "bg-red-50 text-red-700 border-red-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  offline: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

export const StatusBadge = ({ status, className = "" }) => {
  const s = String(status || "").toLowerCase();
  const style = STATUS_STYLES[s] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <Badge variant="outline" data-testid={`status-badge-${s}`} className={`${style} font-medium capitalize ${className}`}>
      {s.replace(/_/g, " ")}
    </Badge>
  );
};

export const KpiCard = ({ label, value, sub, icon: Icon, money = false, testId }) => (
  <Card className="shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-muted-foreground truncate">{label}</p>
          <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1 font-heading truncate">
            {money ? fmtINR(value) : (typeof value === "number" || (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))))
              ? Number(value || 0).toLocaleString("en-IN") : (value ?? "-")}
          </p>
          {sub && <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
        {Icon && (
          <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-accent-foreground" />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="header-wash rounded-xl px-4 py-4 sm:px-5 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    <div>
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight font-heading">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
  </div>
);

export const Spinner = ({ label = "Loading..." }) => (
  <div className="flex items-center justify-center py-16 text-muted-foreground gap-2" data-testid="loading-spinner">
    <Loader2 className="h-5 w-5 animate-spin" />
    <span className="text-sm">{label}</span>
  </div>
);

export const EmptyState = ({ title = "Nothing here yet", subtitle, action }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="empty-state">
    <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
      <Inbox className="h-6 w-6 text-muted-foreground" />
    </div>
    <p className="font-medium">{title}</p>
    {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm">{subtitle}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);
